import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdEdit } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function RenameModal({ targetId, currentName, currentUnit, targetType, onClose, onSuccess }) {
    const { t } = useLanguage();
    const [newName, setNewName] = useState(currentName || '');
    const [newUnit, setNewUnit] = useState(currentUnit || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const getTypeLabels = () => {
        switch (targetType) {
            case 'system': return { title: t('renameModal.systemTitle'), label: t('renameModal.systemLabel'), desc: t('renameModal.systemDesc') };
            case 'sensor': return { title: t('renameModal.sensorTitle'), label: t('renameModal.sensorLabel'), desc: t('renameModal.sensorDesc') };
            case 'counter': return { title: t('renameModal.counterTitle'), label: t('renameModal.counterLabel'), desc: t('renameModal.counterDesc') };
            default: return { title: t('renameModal.defaultTitle'), label: t('renameModal.defaultLabel'), desc: '' };
        }
    };

    const { title, label, desc } = getTypeLabels();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newName.trim()) { setError(t('renameModal.nameRequired')); return; }
        if (newName.trim() === currentName && newUnit.trim() === (currentUnit || '')) { onClose(); return; }

        try {
            setLoading(true);
            let endpoint = '';
            let payload = {};
            
            if (targetType === 'system') {
                endpoint = `/systems/${targetId}`;
                payload = { systemName: newName.trim() };
            } else if (targetType === 'sensor') {
                endpoint = `/sensors/${targetId}`;
                payload = { sensorName: newName.trim(), unit: newUnit.trim() };
            } else if (targetType === 'counter') {
                endpoint = `/counters/${targetId}`;
                payload = { counterName: newName.trim(), unit: newUnit.trim() };
            }

            await api.put(endpoint, payload);
            toast.success(t('renameModal.updated'));
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('renameModal.updateFailed');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    className="modal"
                    style={{ maxWidth: 440 }}
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.25 }}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(79,142,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                <MdEdit />
                            </div>
                            {title}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                                {desc}
                            </p>
                            <div className="form-group">
                                <label className="form-label">{label}</label>
                                <input
                                    className="form-input"
                                    type="text"
                                    value={newName}
                                    onChange={(e) => { setNewName(e.target.value); setError(''); }}
                                    disabled={loading}
                                    autoFocus
                                />
                                {error && <div className="form-error">{error}</div>}
                            </div>
                            
                            {(targetType === 'sensor' || targetType === 'counter') && (
                                <div className="form-group">
                                    <label className="form-label">{t('renameModal.unitLabel')}</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        value={newUnit}
                                        onChange={(e) => setNewUnit(e.target.value)}
                                        disabled={loading}
                                        placeholder={t('renameModal.unitPlaceholder')}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? <><span className="spinner spinner-sm" /> {t('common.saving')}</> : t('common.save')}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
